import { Users } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import type { User } from '../../types'
import { ExpandableTile, StatRow } from '../../components/dashboard'
import { formatRole, getStatusIcon } from './helpers'

const RECENT_PREVIEW_LIMIT = 3
const HOSTNAME_TRUNCATE_PX = 120

interface UsersTileProps {
  totalUsers: number
  usersByStatus: Record<string, number>
  usersByRole: Record<string, number>
  recentUsers: User[]
  isPlatformAdmin: boolean
}

function StatusBlock({ byStatus }: { byStatus: Record<string, number> }): React.ReactElement {
  const { t } = useTranslation('admin')
  return (
    <div className="space-y-1">
      <p
        className="label-mono text-xs font-medium tracking-wider uppercase"
        style={{ color: 'var(--text-muted)' }}
      >
        {t('stats.byStatus')}
      </p>
      <StatRow label={t('stats.active')} value={byStatus.active ?? 0} highlight />
      <StatRow label={t('stats.pending')} value={byStatus.pending ?? 0} />
      <StatRow label={t('stats.suspended')} value={byStatus.suspended ?? 0} />
    </div>
  )
}

function RoleBlock({ byRole }: { byRole: Record<string, number> }): React.ReactElement {
  const { t } = useTranslation('admin')
  return (
    <div className="space-y-1">
      <p
        className="label-mono text-xs font-medium tracking-wider uppercase"
        style={{ color: 'var(--text-muted)' }}
      >
        {t('stats.byRole')}
      </p>
      {Object.entries(byRole).map(([role, count]) => (
        <StatRow key={role} label={formatRole(role)} value={count} />
      ))}
    </div>
  )
}

function RecentUsersBlock({ users }: { users: User[] }): React.ReactElement {
  const { t } = useTranslation('admin')
  return (
    <div className="space-y-1 border-t pt-2" style={{ borderColor: 'var(--border)' }}>
      <p
        className="label-mono text-xs font-medium tracking-wider uppercase"
        style={{ color: 'var(--text-muted)' }}
      >
        {t('stats.recentUsers')}
      </p>
      {users.slice(0, RECENT_PREVIEW_LIMIT).map((recentUser) => (
        <div key={recentUser.id} className="flex items-center justify-between py-1">
          <div className="flex items-center gap-2">
            {getStatusIcon(recentUser.status)}
            <span
              className="truncate text-sm"
              style={{ color: 'var(--text-dim)', maxWidth: `${String(HOSTNAME_TRUNCATE_PX)}px` }}
            >
              {recentUser.displayName ?? recentUser.username}
            </span>
          </div>
          <span className="text-xs" style={{ color: 'var(--text-muted)' }}>
            {formatRole(recentUser.role)}
          </span>
        </div>
      ))}
    </div>
  )
}

export function UsersTile(props: UsersTileProps): React.ReactElement {
  const { totalUsers, usersByStatus, usersByRole, recentUsers, isPlatformAdmin } = props
  const { t } = useTranslation('admin')
  return (
    <ExpandableTile
      title={t('stats.totalUsers')}
      value={totalUsers}
      icon={Users}
      viewAllLink="/admin/users"
    >
      <div className="space-y-3">
        <StatusBlock byStatus={usersByStatus} />
        <RoleBlock byRole={usersByRole} />
        {!isPlatformAdmin && recentUsers.length > 0 && <RecentUsersBlock users={recentUsers} />}
      </div>
    </ExpandableTile>
  )
}
